import { observer } from 'mobx-react'
import { useOutletContext, useParams } from 'react-router'
import { PARAM } from '@App/navigation/param'
import React, { useContext, useEffect, useState } from 'react'
import { StoresContext } from '@App/contexts'
import RecordingGroupCategory from '@App/enum/recording-group-category'
import { getNumTitles } from '@App/helpers/pagination/get-num-titles'
import { PaginationBody } from '@Entities/pagination'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'
import SuggestedMatchMetadata from '@Components/MatchComponents/v2/SuggestedMatchMetadata'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import TabsWrapperComponent from '@Components/TabsWrapperComponent'
import BackButton from '@Components/Buttons/BackButton'
import ContentHeader from '@Components/ContentHeader'
import { createRecordingGroupTabs } from './recording-group-tabs-factory'
import './index.scss'

const MatchHistoryViewRecordingGroups = () => {
    const params = useParams<typeof PARAM>()
    const { matchHistoryStore, preloaderStore, songwriterStore } = useContext(StoresContext)
    const [loading, setLoading] = useState<boolean>(false)
    const { isSongwriterHub } = useOutletContext<{ isSongwriterHub: boolean }>()

    if (!params.submissionId) {
        return <></>
    }

    const suggestionId = parseInt(params.submissionId!, 10)

    const getRecordingGroupInfo = (status: RecordingGroupCategory) => {
        const foundData = matchHistoryStore.recordingGroupsDetails[status]
        const totalCount = foundData ? foundData.totalElements : 0
        const paginationData = matchHistoryStore.recordingGroupsDetailsPagination[status]
        const numTitles =
            paginationData &&
            getNumTitles(totalCount, paginationData.currentPage, matchHistoryStore.recordingGroupsResultsLimit)

        return { totalCount, paginationData, numTitles }
    }

    const recordingGroupInfo = {
        [RecordingGroupCategory.Rejected]: getRecordingGroupInfo(RecordingGroupCategory.Rejected),
        [RecordingGroupCategory.Pending]: getRecordingGroupInfo(RecordingGroupCategory.Pending),
        [RecordingGroupCategory.Accepted]: getRecordingGroupInfo(RecordingGroupCategory.Accepted),
    }

    useEffect(() => {
        const loadAllStatuses = (songwriterId?: string) => {
            const statuses = [
                RecordingGroupCategory.Pending,
                RecordingGroupCategory.Rejected,
                RecordingGroupCategory.Accepted,
            ]
            const limit = matchHistoryStore.recordingGroupsResultsLimit
            const pageBody = { limit, page: 1 }
            const loads = statuses.map((status) =>
                isSongwriterHub
                    ? matchHistoryStore.loadSongwriterRecordingGroupDetails(
                          songwriterId!,
                          suggestionId,
                          status,
                          pageBody
                      )
                    : matchHistoryStore.loadRecordingGroupDetails(suggestionId, status, pageBody)
            )
            return Promise.all(loads)
        }

        if (matchHistoryStore.selectedMatch && matchHistoryStore.selectedMatch.suggestedMatchId !== suggestionId) {
            matchHistoryStore.resetSelectedMatch()
        }

        if (!matchHistoryStore.selectedMatch) {
            preloaderStore.openTransparentPreloader(true)
            if (isSongwriterHub) {
                matchHistoryStore
                    .loadSongwriterSuggestedMatch(songwriterStore.currentSongwriterId, suggestionId)
                    .then(() => loadAllStatuses(songwriterStore.currentSongwriterId))
                    .finally(() => preloaderStore.openTransparentPreloader(false))
            } else {
                matchHistoryStore
                    .loadSuggestedMatch(suggestionId)
                    .then(() => loadAllStatuses())
                    .finally(() => preloaderStore.openTransparentPreloader(false))
            }
        }
    }, [])

    useEffect(() => {
        analyticsSendPage('match_history_recording_groups')
    }, [])

    const handlePageChange = (status: RecordingGroupCategory, pagination: PaginationBody) => {
        setLoading(true)
        const loader = isSongwriterHub
            ? matchHistoryStore.loadSongwriterRecordingGroupDetails(
                  songwriterStore.currentSongwriterId,
                  suggestionId,
                  status,
                  pagination
              )
            : matchHistoryStore.loadRecordingGroupDetails(suggestionId, status, pagination)
        loader.finally(() => setLoading(false))
    }

    const handleLimitChange = (status: RecordingGroupCategory, newLimit: number) => {
        matchHistoryStore.setRecordingGroupResultsLimit(newLimit)
        handlePageChange(status, { limit: newLimit, page: 1 })
    }

    const internalHomeTabs = createRecordingGroupTabs({
        recordingGroupInfo,
        resultsLimit: matchHistoryStore.recordingGroupsResultsLimit,
        onPageChange: handlePageChange,
        onLimitChange: handleLimitChange,
    })

    const foundSelectedMatch = matchHistoryStore.selectedMatch

    return (
        <div className="match-recording-groups-wrapper">
            {foundSelectedMatch && (
                <>
                    <ContentHeader
                        title={foundSelectedMatch?.workTitle}
                        descriptionElement={<SuggestedMatchMetadata suggestedMatch={foundSelectedMatch} />}
                        navLeft={<BackButton />}
                    />

                    <section className="match-recording-group-head" />
                    <section className="recording-groups-table">
                        {loading ? (
                            <PointPreloader />
                        ) : (
                            <TabsWrapperComponent
                                tabs={internalHomeTabs}
                                defaultTab={internalHomeTabs[0]}
                                preventScrollReset
                            />
                        )}
                    </section>
                </>
            )}
        </div>
    )
}

export default observer(MatchHistoryViewRecordingGroups)
